// Filter panel rows → the backend filter inputs of the admin Recordings and
// Sequences tabs. Several rows on the same column are combined, not
// overridden.
//
// project_management#575: list columns split on commas, semicolons and any
// whitespace (a pasted column), the same rules as the backend
// (`zive-backend` `shared/filters/listInput`). Values are sent comma-joined,
// which every backend version understands. `comment` is free text: only commas
// separate its values, so its rows are passed on as typed.
import type { FilterRow } from '../datagrid/CustomFilterPanel';
import { splitIdentifierList } from '../datagrid/filterListInput';

export interface RecordingsFilterInput {
  userId?: string;
  id?: string;
  fileName?: string;
  userEmail?: string;
  flags?: string[];
  comment?: string;
}

export interface SequencesFilterInput {
  userId?: string;
  id?: string;
  userEmail?: string;
  status?: string;
}

/** Non-blank row values per column, trimmed, in row order. */
function groupByColumn(rows: FilterRow[]): Record<string, string[]> {
  const grouped: Record<string, string[]> = {};

  rows.forEach((row) => {
    const value = row.value.trim();

    if (!value) return;
    if (!grouped[row.column]) grouped[row.column] = [];
    grouped[row.column].push(value);
  });

  return grouped;
}

/** Every token of every row of a list column. */
const tokensOf = (values: string[] | undefined): string[] =>
  (values ?? []).flatMap((value) => splitIdentifierList(value));

/** A list column as the comma-joined string the backend takes; undefined when empty. */
const listOf = (values: string[] | undefined, map: (token: string) => string = (t) => t) => {
  const tokens = tokensOf(values).map(map);

  return tokens.length ? tokens.join(',') : undefined;
};

/** Drop undefined keys so an empty filter stays `{}` (= no filter). */
function compact<T extends object>(input: T): T {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as T;
}

export function buildRecordingsFilterInput(rows: FilterRow[]): RecordingsFilterInput {
  const grouped = groupByColumn(rows);
  const flags = tokensOf(grouped.flags).map((flag) => flag.toUpperCase());

  return compact({
    userId: listOf(grouped.userId),
    id: listOf(grouped.id),
    fileName: listOf(grouped.fileName),
    userEmail: listOf(grouped.userEmail),
    flags: flags.length ? flags : undefined,
    comment: grouped.comment ? grouped.comment.join(',') : undefined,
  });
}

export function buildSequencesFilterInput(rows: FilterRow[]): SequencesFilterInput {
  const grouped = groupByColumn(rows);

  return compact({
    userId: listOf(grouped.userId),
    id: listOf(grouped.id),
    userEmail: listOf(grouped.userEmail),
    status: listOf(grouped.status, (status) => status.toLowerCase()),
  });
}
